import {parseRunId} from '../../../../apolloClient';
import * as Filter from '../../../../util/filters';
import type {Filter as FilterType} from '../../../../util/filterTypes';
import {Key} from '../../../../util/runTypes';

/**
 * Calculate the runs query limit accounting for baseline and pinned runs.
 *
 * Args:
 *     isSingleRunMode (boolean): Whether the view targets a single run.
 *     pageSize (number): Desired total rows for the panel before adjustments.
 *     includeBaseline (boolean): Whether the baseline run is fetched separately.
 *     pinnedCount (number): Number of pinned runs fetched outside the main query.
 *
 * Returns:
 *     number: The adjusted limit for the main runs query.
 *
 * Examples:
 *     >>> calculateRunsLimit(true, 25, true, 3)
 *     1
 *     >>> calculateRunsLimit(false, 10, true, 2)
 *     7
 */
export function calculateRunsLimit(
  isSingleRunMode: boolean,
  pageSize: number,
  includeBaseline: boolean,
  pinnedCount: number
): number {
  if (isSingleRunMode) {
    return 1;
  }
  return Math.max(1, pageSize - Number(includeBaseline) - pinnedCount);
}

/**
 * Constructs filter and metadata for baseline run query.
 *
 * Returns an object with:
 * - filters: MongoDB filter JSON string for fetching the baseline run
 * - include: Whether to include the baseline run in the query
 * - runName: The parsed name of the baseline run (or null if not present)
 */
export function getBaselineRunQueryParams(baselineRunId: string | undefined) {
  const runName = baselineRunId ? parseRunId({id: baselineRunId}) : null;

  return {
    filters: runName
      ? JSON.stringify(
          Filter.toMongo({
            op: '=',
            key: {section: 'run', name: 'name'},
            value: runName,
          })
        )
      : undefined,
    include: !!runName,
    runName,
  };
}

/**
 * Constructs filter and metadata for pinned runs query.
 *
 * Returns an object with:
 * - filters: MongoDB filter JSON string for fetching pinned runs
 * - include: Whether to include pinned runs in the query
 * - runNames: Array of parsed run names
 */
export function getPinnedRunsQueryParams(
  pinnedRunIds: string[] | undefined,
  isSingleRunMode: boolean
) {
  const runNames =
    pinnedRunIds
      ?.map(id => parseRunId({id}))
      .filter((name): name is string => name != null) ?? [];

  return {
    filters:
      runNames.length > 0
        ? JSON.stringify(
            Filter.toMongo({
              op: 'IN',
              key: {section: 'run', name: 'name'},
              value: runNames,
            })
          )
        : undefined,
    include: !isSingleRunMode && runNames.length > 0,
    runNames,
  };
}

/**
 * Applies exclusions to the base filters to prevent baseline/pinned runs
 * from appearing in the main runs query.
 *
 * In single-run mode, no exclusions are needed (main query already targets specific run).
 * In multi-run mode, excludes baseline and pinned runs to prevent overlap.
 */
export function applyRunExclusions(
  baseFilters: FilterType<Key>,
  options: {
    isSingleRunMode: boolean;
    baselineRunName: string | null;
    pinnedRunNames: string[];
  }
): FilterType<Key> {
  if (options.isSingleRunMode) {
    return baseFilters;
  }

  const exclusionFilters: FilterType<Key>[] = [];

  if (options.baselineRunName) {
    exclusionFilters.push({
      op: '!=',
      key: {section: 'run', name: 'name'},
      value: options.baselineRunName,
    });
  }

  if (options.pinnedRunNames.length > 0) {
    exclusionFilters.push({
      op: 'NIN',
      key: {section: 'run', name: 'name'},
      value: options.pinnedRunNames,
    });
  }

  return exclusionFilters.length > 0
    ? Filter.And([baseFilters, ...exclusionFilters])
    : baseFilters;
}
